import React, { type PropsWithChildren, useRef } from 'react';
import classNames from 'classnames';
import { ApplicationView as view } from '@aha-app/mvc';
import { useDrop } from 'react-dnd';
import styles from './styles.module.css';
import ItemPlacement from 'components/framework/models/ItemPlacement';
import type DraggableItem from '../DraggableItem';
import { type SelectableItemController } from '../SelectableItem';
import type ListDropHandler from '../ListDropHandler';

type ListSpacerDropTargetProps = {
  // A class to add to the wrapping div.
  className?: string;
  // The drop handler for the container.
  dropHandler: ListDropHandler;
  // Whether to place the items at the start or end of the target list.
  placeAtEnd?: boolean;
};

/**
 * The ListSpacerDropTarget is useful to provide a spacer before/after an ItemList, to allow
 * hovering/dropping of items at the start or end of a list.
 */
const ListSpacerDropTarget: React.FC<
  PropsWithChildren<ListSpacerDropTargetProps>
> = ({
  children = null,
  className = null,
  dropHandler,
  placeAtEnd = false,
}) => {
  const ref = useRef(null);

  const [, drop] = useDrop({
    accept: dropHandler.accepts,
    hover(draggedItem: DraggableItem<SelectableItemController>, monitor) {
      if (!dropHandler.canHover({ draggedItem })) return;

      dropHandler.onReorder({
        draggedItem,
        placement: placeAtEnd ? ItemPlacement.bottom() : ItemPlacement.top(),
      });
    },
    canDrop(draggedItem, monitor) {
      return dropHandler.canHover({ draggedItem });
    },
    drop(draggedItem, monitor) {
      if (!monitor.didDrop()) {
        dropHandler.onDrop({ draggedItem });
      }
    },
  });

  drop(ref);

  return (
    <div className={classNames(className, styles.listDropSpacer)} ref={ref}>
      {children}
    </div>
  );
};

export default view(ListSpacerDropTarget);
